/**
 * AudienceBuilder: the four audience selectors of a send, combined (plan
 * docs/plans/lms-plan.md 8.2 step 2 "four selectors that combine: everyone at
 * one or more sites, platform roles, groups, individual people", 5.4, 18.16;
 * WP-43; reused by WP-46's send-for-training).
 *
 * Props (stable):
 *   value      CampaignAudience                     { everyone_at_sites, platform_roles, group_ids, user_ids }
 *   onChange   (audience: CampaignAudience) => void  a NEW object on every change
 *   siteName?  string | null                        the acting site, named under the roles (they resolve there)
 *   disabled?  boolean
 *
 * The server takes the UNION of the four (anyone named by any selector), so
 * each section says so. The sections are SitePicker, RolePicker, GroupPicker
 * (profiles and saved audiences) and UserPicker; a count of the choices sits
 * in each section's heading. Who that actually reaches is the preview's job
 * (useCampaignPreview + AudiencePreview).
 */
import type { ReactNode } from 'react';
import type { CampaignAudience } from '../../api/campaigns';
import type { LmsPlatformRole } from '../../api/client';
import GroupPicker from './GroupPicker';
import RolePicker from './RolePicker';
import SitePicker from './SitePicker';
import UserPicker from './UserPicker';
import { cardCls, metaCls, sectionTitleCls } from './shared';

export interface AudienceBuilderProps {
  value: CampaignAudience;
  onChange: (audience: CampaignAudience) => void;
  siteName?: string | null;
  disabled?: boolean;
}

function Section({ title, hint, count, testId, children }: { title: string; hint: string; count: number; testId: string; children: ReactNode }) {
  return (
    <section className={cardCls} data-testid={testId} aria-label={title}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className={sectionTitleCls}>{title}</h3>
        {count > 0 && <span className={metaCls}>{count.toLocaleString()} chosen</span>}
      </div>
      <p className={`${metaCls} mb-3`}>{hint}</p>
      {children}
    </section>
  );
}

export function AudienceBuilder({ value, onChange, siteName = null, disabled = false }: AudienceBuilderProps) {
  const set = (patch: Partial<CampaignAudience>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3" data-testid="audience-builder">
      <p className={metaCls}>Anyone named by any of the four is included; each person is counted once.</p>
      <div className="grid gap-3 lg:grid-cols-2">
        <Section
          title="Everyone at a site"
          hint="Every active person who can reach the site."
          count={value.everyone_at_sites.length}
          testId="audience-sites"
        >
          <SitePicker
            value={value.everyone_at_sites}
            onChange={(ids) => set({ everyone_at_sites: ids })}
            label="Sites"
            disabled={disabled}
          />
        </Section>
        <Section
          title="By platform role"
          hint="People matched by their platform role."
          count={value.platform_roles.length}
          testId="audience-roles"
        >
          <RolePicker
            value={value.platform_roles}
            onChange={(roles: LmsPlatformRole[]) => set({ platform_roles: roles })}
            siteName={siteName}
            label="Roles"
            disabled={disabled}
          />
        </Section>
        <Section
          title="Groups and profiles"
          hint="The active members of each group you tick."
          count={value.group_ids.length}
          testId="audience-groups"
        >
          <GroupPicker value={value.group_ids} onChange={(ids) => set({ group_ids: ids })} label="Groups" disabled={disabled} />
        </Section>
        <Section
          title="Individual people"
          hint="Search by name or email and tick each person."
          count={value.user_ids.length}
          testId="audience-people"
        >
          <UserPicker value={value.user_ids} onChange={(ids) => set({ user_ids: ids })} label="People" disabled={disabled} />
        </Section>
      </div>
    </div>
  );
}

export default AudienceBuilder;
